import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ExternalLink } from '@/components/ExternalLink';
import { TaskPopup } from '@/components/tasks/TaskPopup';

/**
 * A web address opened from a link in the chat or a task's comments (SF-T-160), framed, with its
 * address and Open in a new tab above it. Sites known to refuse frames never come here (canFrame);
 * others may still refuse, and the page can't tell, so the header says what a blank frame means.
 * The frame is sandboxed: the site runs in its own origin, and its pop-ups open as tabs.
 */
export function WebLinkDialog({ url, onClose }: { url: string; onClose: () => void }) {
  return (
    <TaskPopup open onClose={onClose} title="Web page">
      <div className="flex min-w-0 flex-col max-md:h-[calc(100dvh-2rem)] md:h-full">
        <div
          data-pinned-bar
          className="flex min-w-0 shrink-0 items-center gap-2 border-b bg-background max-md:-mx-4 max-md:-mt-4 max-md:px-2 max-md:py-1 md:px-3 md:py-2"
        >
          <div className="min-w-0 flex-1 px-1">
            <p className="truncate text-sm font-medium">{url}</p>
            <p className="truncate text-2xs text-muted-foreground">Blank? The site doesn’t allow being shown here: open it in a new tab.</p>
          </div>
          <div className="shrink-0 px-1 text-sm max-md:py-3">
            <ExternalLink href={url} strong>
              Open in a new tab
            </ExternalLink>
          </div>
          <Button variant="ghost" size="icon-sm" className="max-md:size-11" aria-label="Close" data-hint="Close" onClick={onClose}>
            <X />
          </Button>
        </div>
        <iframe
          key={url}
          src={url}
          title={url}
          className="min-h-0 w-full flex-1 bg-background max-md:-mx-4 max-md:mb-[-1rem] max-md:w-[calc(100%+2rem)]"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads"
          referrerPolicy="no-referrer"
          loading="eager"
        />
      </div>
    </TaskPopup>
  );
}
